import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Text, Icon } from '@chakra-ui/react';
import { AddIcon, MinusIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import {
  getRangeWindow,
  getRangeDateWindow,
  getMonthsInWindow,
  getPeriodsInWindow,
  granularityForRange,
  buildBalanceSeries,
  computeDeltaPct,
  periodEnd,
} from '../../utils/movementBuckets';
import { fetchUnrealizedGains, getAccountBalanceHistory } from '../../api/endpointsBackend';
import { isDebtAccount } from '../../utils/accountTypes';
import { holdsForeign } from '../../utils/rateSources';
import { formatMoney, formatPhoneBalance } from '../../utils/formatters';
import RangeSelector from '../shared/RangeSelector';

import Card from '../shared/Card';
import Figure from '../shared/Figure';
import TimeSeriesChart, { chartColor } from '../shared/TimeSeriesChart';
import { TONE, toneOf } from './tones';
import DeltaPill from './DeltaPill';
// Asset-style accounts: more = better. The line is the assets colour; the
// gradient goes from a pale step of it (low values) to the full colour (high values).
const ASSET_LOW = pale(chartColor('redesign.accent.assets'));
const ASSET_HIGH = chartColor('redesign.accent.assets');

// Debt-style accounts: more = worse. Low debt is green, high debt is the debt colour.
const DEBT_LOW = chartColor('redesign.accent.green');
const DEBT_HIGH = chartColor('redesign.accent.expense');

// Investment accounts that lost money — chart goes red so a losing arc reads
// as a loss at a glance, not a "successful round trip".
const LOSS_LOW = pale(chartColor('redesign.accent.red'));
const LOSS_HIGH = chartColor('redesign.accent.red');


// `t` of the way from colour a to colour b, t clamped to [0, 1] by callers.
// The colours are theme variables, so the browser does the mixing.
function mixColor(a, b, t) {
  return `color-mix(in srgb, ${b} ${(t * 100).toFixed(1)}%, ${a})`;
}

// A pale step of a colour: mixed into the surface the chart is drawn on.
function pale(color) {
  return mixColor(chartColor('redesign.bg.surface'), color, 0.45);
}

const INVESTMENT_TYPES = new Set(['investment', 'stock', 'funds']);

// The phone's account sheet keeps four ranges, so each pill is a 44px tap
// that fits a 320px screen, and ALL stays the default.
const PHONE_RANGES = ['1M', '3M', '1Y', 'ALL'];
const PHONE_PILL = {
  flex: '1', maxW: '72px', minH: '44px', px: 0, py: 0, fontSize: '13px', whiteSpace: 'nowrap',
};

/**
 * Per-account balance trend with horizontal gradient stroke.
 *
 * The chart manages its own range state — independent from the page-level
 * overview filter, so changing it doesn't refetch or affect the movements
 * list below. All series derived client-side from the cached `buckets`.
 *
 * Color encoding depends on account type (see `isDebtAccount`):
 *   - Asset-style → the assets colour only, intensity tracks the value
 *   - Debt-style  → green (low) → the debt colour (high), to signal increasing debt
 *
 * For investment accounts, an extra row of badges shows lifetime ROI and
 * annual effective rate (computed server-side via XIRR over Transfer flows).
 *
 * `phone` is the phone's account sheet: fewer, wider range pills, and the
 * balance under the phone's amount rule (no cents from four digits up).
 *
 * `memberIds` draws several accounts as one line: a broker with its holdings,
 * so a buy (cash into a holding) is no drop. `headline={false}` leaves the
 * balance and its pills to whoever shows them above, and `caption` says what
 * the line is.
 *
 * `header` is drawn left of the balance, on its line (the account panel puts
 * the account's name there), and `frameless` draws the chart on the page
 * instead of in a card.
 *
 * `atMarket` (`{ accountId, version }`) draws a broker's holdings at their
 * latest price on each point's day instead of at cost: what they stood over
 * their cost that day is added to the line. `version` changes when a price
 * does, so the chart asks again.
 */
export default function AccountBalanceChart({
  account, buckets, currency, roi, convert = null, phone = false,
  memberIds = null, headline = true, caption = null, header = null, headerTop = null, frameless = false,
  onRangeChange = null, headlinePills = null, headlineFigure = null, headerFoot = null,
  atMarket = null,
}) {
  const { t } = useTranslation('accounts');
  // Default to ALL so users immediately see the account's full balance arc;
  // a 1M default collapses to one or two months and reads as a flat line for
  // stable accounts.
  const [range, setRange] = useState('ALL');
  // The panel's holding and broker summaries measure their return over the
  // same range.
  useEffect(() => { if (onRangeChange) onRangeChange(range); }, [range, onRangeChange]);

  // MI-307: the panel's range is per-account and independent from the page's,
  // so it cannot reuse whatever window the page happened to fetch — it pulls
  // its own fine-grained series, scoped to this one account (a tiny payload).
  // Monthly ranges (1Y / ALL) keep deriving from the cached `buckets` prop.
  const granularity = granularityForRange(range);
  const dateWindow = useMemo(() => getRangeDateWindow(range), [range]);
  const [fine, setFine] = useState(null);
  const fineCache = useRef(new Map());

  const memberKey = (memberIds || []).join(',');
  useEffect(() => { fineCache.current.clear(); setFine(null); }, [account?.id, memberKey]);

  useEffect(() => {
    const accountId = account?.id;
    if (!accountId || granularity === 'month' || !dateWindow.startDate) {
      setFine(null);
      return;
    }
    const cached = fineCache.current.get(range);
    if (cached) { setFine(cached); return; }

    let cancelled = false;
    getAccountBalanceHistory(dateWindow.startDate, dateWindow.endDate, null, {
      granularity,
      includeWindowOpening: true,
      accountIds: memberKey ? memberKey.split(',').map(Number) : [accountId],
    })
      .then(res => {
        const payload = { range, granularity, buckets: res || [] };
        fineCache.current.set(range, payload);
        if (!cancelled) setFine(payload);
      })
      .catch(err => {
        console.error('Failed to fetch account balance history:', err);
        if (!cancelled) setFine(null);
      });
    return () => { cancelled = true; };
  }, [account?.id, memberKey, range, granularity, dateWindow.startDate, dateWindow.endDate]);

  // MI-298 stock view: `convert` = { target, rates } (rates = usd_per_unit map).
  // When active, the series sums ALL of the account's currencies valued at
  // today's rate; balance and pills show ≈ in the target currency.
  const converting = Boolean(convert?.target && convert?.rates?.[convert.target]);
  const displayCur = converting ? convert.target : currency;

  const isDebt = isDebtAccount(account?.account_group, account?.account_type);
  const isInvestment = INVESTMENT_TYPES.has(account?.account_type);
  // Investment accounts color by ROI sign: red when losing money, green
  // otherwise. Falls through to the asset/debt encoding when ROI is null.
  const investmentLost = isInvestment && roi?.lifetime_roi_pct != null && roi.lifetime_roi_pct < 0;
  let lowColor, highColor;
  if (investmentLost) {
    lowColor = LOSS_LOW;
    highColor = LOSS_HIGH;
  } else if (isDebt) {
    lowColor = DEBT_LOW;
    highColor = DEBT_HIGH;
  } else {
    lowColor = ASSET_LOW;
    highColor = ASSET_HIGH;
  }

  const fineActive = Boolean(fine && fine.range === range);
  const activeGranularity = fineActive ? fine.granularity : 'month';
  const ownBuckets = fineActive ? fine.buckets : buckets;
  // The members' buckets, filed under this account so one series adds them.
  const seriesBuckets = useMemo(() => {
    if (!memberKey || !account?.id || !ownBuckets) return ownBuckets;
    const members = new Set(memberKey.split(',').map(Number));
    return ownBuckets
      .filter(b => members.has(b.account_id))
      .map(b => (b.account_id === account.id ? b : { ...b, account_id: account.id }));
  }, [ownBuckets, memberKey, account?.id]);
  // MI-329: a balance only in the display currency is exact, with no "≈".
  const balanceIsConverted = converting && holdsForeign(
    (seriesBuckets || []).filter(b => b.account_id === account?.id).map(b => b.currency),
    convert.target,
  );

  // The chart's points, before the months the account did not exist yet are
  // dropped.
  const basePeriods = useMemo(() => {
    if (!account?.id || !seriesBuckets || seriesBuckets.length === 0) return [];
    if (fineActive) return getPeriodsInWindow(dateWindow.startDate, dateWindow.endDate, activeGranularity);
    const window = getRangeWindow(range);
    return getMonthsInWindow(window.start, window.end, seriesBuckets)
      .map(m => `${m.year}-${String(m.month).padStart(2, '0')}-01`);
  }, [account?.id, seriesBuckets, fineActive, dateWindow, activeGranularity, range]);

  // At market: the holdings' gain over cost on each point's day, by currency.
  const gainDates = useMemo(
    () => (atMarket ? basePeriods.map(p => periodEnd(p, activeGranularity)) : []),
    [atMarket, basePeriods, activeGranularity],
  );
  const gainKey = gainDates.join(',');
  const [gains, setGains] = useState(null);
  const gainCache = useRef(new Map());
  const marketId = atMarket?.accountId;
  const marketVersion = atMarket?.version;
  useEffect(() => { gainCache.current.clear(); }, [marketId, marketVersion]);
  useEffect(() => {
    if (!marketId || !gainKey) { setGains(null); return; }
    const cached = gainCache.current.get(gainKey);
    if (cached) { setGains(cached); return; }
    let cancelled = false;
    fetchUnrealizedGains(marketId, [...new Set(gainKey.split(','))])
      .then(rows => {
        const byDate = {};
        (rows || []).forEach(r => { byDate[r.date] = { ...byDate[r.date], [r.currency]: r.unrealized_gain }; });
        const payload = { key: gainKey, byDate };
        gainCache.current.set(gainKey, payload);
        if (!cancelled) setGains(payload);
      })
      .catch(err => {
        // Drawn at cost rather than not at all.
        console.error('Failed to fetch unrealized gains:', err);
        if (!cancelled) setGains({ key: gainKey, byDate: {} });
      });
    return () => { cancelled = true; };
  }, [marketId, marketVersion, gainKey]);
  const gainsReady = !atMarket || gains?.key === gainKey;

  const { displaySeries, chartPeriods, currentBalance, deltaAbs, deltaPct } = useMemo(() => {
    let periods = basePeriods;
    if (periods.length === 0 || !gainsReady) {
      return { displaySeries: [], chartPeriods: [], currentBalance: 0, deltaAbs: 0, deltaPct: null };
    }
    // At market, each currency's line gets its holdings' gain on that day.
    const withGains = (raw, cur) => (
      atMarket ? raw.map((v, i) => v + (gains.byDate[gainDates[i]]?.[cur] || 0)) : raw
    );
    let series;
    if (converting) {
      // Sum every currency the account holds, valued at today's rate. The
      // debt-abs is applied PER CURRENCY before summing — same convention as
      // the rows/header cards (magnitudes per currency), so all three agree.
      const target = convert.rates[convert.target];
      const curs = [...new Set(
        seriesBuckets.filter(b => b.account_id === account.id).map(b => b.currency).filter(Boolean)
      )];
      let combined = null;
      curs.forEach(cur => {
        const upu = convert.rates[cur];
        if (upu == null) return; // unconvertible currency — excluded (disclosed at page level)
        const raw = withGains(buildBalanceSeries(seriesBuckets, account.id, periods, cur, activeGranularity), cur);
        const perCur = isDebt ? raw.map(v => Math.abs(v)) : raw;
        const factor = upu / target;
        if (!combined) combined = perCur.map(v => v * factor);
        else perCur.forEach((v, i) => { combined[i] = (combined[i] || 0) + v * factor; });
      });
      series = combined || [];
    } else {
      const raw = withGains(buildBalanceSeries(seriesBuckets, account.id, periods, currency, activeGranularity), currency);
      // Debt accounts hold negative cumulative; flip for display.
      series = isDebt ? raw.map(v => Math.abs(v)) : raw;
    }
    // The period axis spans the whole workspace's history, but this account
    // may be younger than that. Plotting the months before it existed draws a
    // long flat zero and then a cliff, which squashes the account's own
    // variation into the top sliver of the chart. Drop that dead prefix.
    let from = 0;
    while (from < series.length - 1 && series[from] === 0) from += 1;
    if (from > 0) {
      series = series.slice(from);
      periods = periods.slice(from);
    }

    const last = series[series.length - 1] || 0;
    const first = series[0] || 0;
    // Skip pct when the baseline is effectively zero — a tiny prev balance
    // produces astronomical percentages that aren't meaningful to the user.
    const baselineUsable = Math.abs(first) > 1;
    return {
      displaySeries: series,
      chartPeriods: periods,
      currentBalance: last,
      deltaAbs: last - first,
      deltaPct: baselineUsable ? computeDeltaPct(last, first) : null,
    };
  }, [basePeriods, gainsReady, atMarket, gains, gainDates, seriesBuckets, activeGranularity, account?.id, currency, isDebt, converting, convert]);

  // Stroke colour varies along the line by value — low balances toward
  // `lowColor`, high toward `highColor`. The shared chart draws the geometry;
  // these are the gradient stops it paints the stroke with.
  const stops = useMemo(() => {
    if (displaySeries.length === 0) return [];
    const min = Math.min(...displaySeries);
    const max = Math.max(...displaySeries);
    const r = max - min || 1;
    if (displaySeries.length === 1) {
      return [{ offset: '0%', color: highColor }, { offset: '100%', color: highColor }];
    }
    return displaySeries.map((v, i) => ({
      offset: `${((i / (displaySeries.length - 1)) * 100).toFixed(2)}%`,
      color: mixColor(lowColor, highColor, (v - min) / r),
    }));
  }, [displaySeries, lowColor, highColor]);

  // For investment accounts that have realized returns, the pill semantics
  // switch to net P/L (gains − losses) instead of period balance change. The
  // current balance can be 0 for closed investments, which makes the
  // period-delta pill read "+COP 0" — useless. Net P/L answers "did I make
  // money on this?" directly.
  let investmentNetPL = isInvestment && roi != null
    && ((roi.gains || 0) > 0 || (roi.losses || 0) > 0)
    ? (roi.gains || 0) - (roi.losses || 0)
    : null;
  // Net P/L is in the ROI row's own currency — scale it to the display
  // currency when converting (percentages are unit-less, left as-is).
  if (investmentNetPL != null && converting && convert.rates[roi.currency] != null) {
    investmentNetPL *= convert.rates[roi.currency] / convert.rates[convert.target];
  }
  const showInvestmentPill = investmentNetPL != null;
  // For debt accounts, going DOWN is good (less debt). Flip the polarity.
  // Effectively-zero change → neutral (no triangle, muted color).
  const effectiveDeltaAbs = showInvestmentPill ? investmentNetPL : deltaAbs;
  const deltaIsConverted = showInvestmentPill
    ? converting && roi.currency !== convert.target
    : balanceIsConverted;
  const effectiveDeltaPct = showInvestmentPill ? roi.lifetime_roi_pct : deltaPct;
  const isNeutralChange = Math.abs(effectiveDeltaAbs) < 0.005;
  const deltaIsGood = isNeutralChange ? null : (isDebt ? effectiveDeltaAbs < 0 : effectiveDeltaAbs > 0);
  const deltaTone = TONE[toneOf(deltaIsGood)];
  // Hide the pill entirely for investment accounts with no returns logged
  // (otherwise it would render "+COP <total deposits>" — misleading).
  const showPill = isInvestment ? showInvestmentPill : displaySeries.length > 1;

  // Two pills (money delta + percentage), in Copilot's 22px pill.
  const pills = (
    <Flex justify="flex-end" gap={1.5} flexShrink={0}>
      <Flex
        align="center"
        gap="4px"
        {...deltaTone}
        h="22px"
        px="8px"
        borderRadius="full"
        fontSize="13px"
        fontWeight="600"
        whiteSpace="nowrap"
      >
        <Icon as={effectiveDeltaAbs >= 0 ? AddIcon : MinusIcon} boxSize="8px" />
        {`${deltaIsConverted ? '≈ ' : ''}${displayCur} ${Math.round(Math.abs(effectiveDeltaAbs)).toLocaleString('en-US')}`}
      </Flex>
      {effectiveDeltaPct != null && (
        <DeltaPill pct={effectiveDeltaPct} good={deltaIsGood} up={effectiveDeltaAbs > 0} />
      )}
    </Flex>
  );
  // The desktop panel's lead figure carries its code small and raised; the
  // phone keeps its own amount rule.
  const balanceText = phone ? (
    <Text fontSize="2xl" fontWeight="bold" color="redesign.heading" lineHeight={1.1} whiteSpace="nowrap">
      {balanceIsConverted && '≈ '}
      {formatPhoneBalance(displayCur, currentBalance)}
    </Text>
  ) : (
    <Flex align="baseline" gap={1}>
      {balanceIsConverted && <Text as="span" color="redesign.text.muted" fontSize="sm">≈</Text>}
      {/* Copilot's balance sits beside the name at about 20px. */}
      <Figure size="card" code={displayCur} amount={formatMoney(currentBalance, displayCur, true)} />
    </Flex>
  );
  const Frame = frameless ? Box : Card;
  const frameProps = frameless ? { mb: 5 } : { borderRadius: 'lg', p: 4, mb: 5 };

  return (
    <Frame {...frameProps}>
      {header && headerTop ? (
        // Copilot's two lines: the pills on the account's line, the balance
        // beside the name, so the name keeps the width the pills would take.
        <Box mb={3}>
          {/* A holding or broker brings its own pills and value (its return and
              what it is worth), drawn where every account's are. */}
          <Flex justify="space-between" align="center" gap={4} mb={2}>
            <Box flex={1} minW={0}>{headerTop}</Box>
            {headlinePills || (headline && showPill && pills)}
          </Flex>
          <Flex justify="space-between" align="flex-start" gap={4}>
            <Box flex={1} minW={0}>{header}</Box>
            {headlineFigure
              ? <Box flexShrink={0} pt="2px">{headlineFigure}</Box>
              : headline && <Box flexShrink={0} pt="2px">{balanceText}</Box>}
          </Flex>
          {headerFoot}
        </Box>
      ) : header ? (
        <Flex justify="space-between" align="flex-start" gap={4} mb={3}>
          <Box flex={1} minW={0}>{header}</Box>
          {headline && (
            <Flex direction="column" align="flex-end" flexShrink={0}>
              {showPill && <Box mb={2}>{pills}</Box>}
              {balanceText}
            </Flex>
          )}
        </Flex>
      ) : headline && (
        <>
          {showPill && <Box mb={2}>{pills}</Box>}
          <Flex justify="flex-end" mb={3}>{balanceText}</Flex>
        </>
      )}

      {caption && (
        <Text fontSize="xs" color="redesign.text.muted" mb={1}>{caption}</Text>
      )}

      {/* Chart */}
      {!gainsReady ? (
        <Box h="100px" my={3} />
      ) : displaySeries.length === 0 ? (
        <Text fontSize="sm" color="redesign.text.muted" textAlign="center" py={8}>
          {t('balanceChart.noHistory')}
        </Text>
      ) : (
        <TimeSeriesChart
          height={100}
          my={3}
          periods={chartPeriods}
          granularity={activeGranularity}
          series={[{
            key: `balance-${account?.id ?? 'x'}`,
            values: displaySeries,
            color: highColor,
            strokeWidth: 3,
            fill: true,
            gradientStops: stops,
          }]}
        />
      )}

      {/* Range pills */}
      <RangeSelector
        value={range}
        onChange={setRange}
        ranges={phone ? PHONE_RANGES : undefined}
        pillProps={phone ? PHONE_PILL : undefined}
      />

      {isInvestment && roi && (
        ((roi.gains || 0) > 0 || (roi.losses || 0) > 0) ? (
          <Flex justify="center" gap={4} mt={3} fontSize="xs" color="redesign.text.muted">
            <Text>
              {t('balanceChart.roi')}{' '}
              <Text as="span" fontWeight="600" color={
                roi.lifetime_roi_pct == null ? 'redesign.text.muted'
                  : roi.lifetime_roi_pct >= 0 ? 'redesign.amount.income' : 'redesign.accent.red'
              }>
                {roi.lifetime_roi_pct == null ? '—' : `${roi.lifetime_roi_pct >= 0 ? '+' : ''}${roi.lifetime_roi_pct.toFixed(2)}%`}
              </Text>
            </Text>
            <Text color="redesign.border">•</Text>
            <Text>
              <Text as="span" fontWeight="600" color={
                roi.annual_effective_pct == null ? 'redesign.text.muted'
                  : roi.annual_effective_pct >= 0 ? 'redesign.amount.income' : 'redesign.accent.red'
              }>
                {roi.annual_effective_pct == null ? '—' : `${roi.annual_effective_pct >= 0 ? '+' : ''}${roi.annual_effective_pct.toFixed(2)}%`}
              </Text>
              {' '}{t('balanceChart.annualEffective')}
            </Text>
          </Flex>
        ) : (
          <Text mt={3} fontSize="xs" color="redesign.text.muted" textAlign="center" fontStyle="italic">
            {t('balanceChart.noReturns')}
          </Text>
        )
      )}
    </Frame>
  );
}
